import {ActionList, ActionMenu, Button, Spinner} from '@primer/react'
import {CopilotIcon, TriangleDownIcon} from '@primer/octicons-react'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import styles from './ModelPicker.module.css'

export interface ModelOption {
  id: string
  name: string
  icon?: string
  multiplier?: number
}

interface ModelsResponse {
  models: ModelOption[]
  current_model?: string
}

interface ModelPickerProps {
  modelsUrl: string
  onModelChange?: (modelId: string) => void
}

export function ModelPicker({modelsUrl, onModelChange}: ModelPickerProps) {
  const [models, setModels] = useState<ModelOption[]>([])
  const [selectedModelId, setSelectedModelId] = useState('auto')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    async function fetchModels() {
      setIsLoading(true)
      try {
        const response = await verifiedFetchJSON(modelsUrl)
        if (response.ok) {
          const data: ModelsResponse = await response.json()
          setModels(data.models)
          if (data.current_model) {
            setSelectedModelId(data.current_model)
            onModelChange?.(data.current_model)
          }
        }
      } catch {
        // Silently fail — picker will show "Auto" only
      } finally {
        setIsLoading(false)
      }
    }

    void fetchModels()
  }, [modelsUrl, onModelChange])

  const handleSelect = useCallback(
    (modelId: string) => {
      setSelectedModelId(modelId)
      onModelChange?.(modelId)
    },
    [onModelChange],
  )

  const selectedModel = models.find(m => m.id === selectedModelId)
  const displayName = selectedModel?.name ?? 'Auto'

  const allModels = useMemo(
    () => [{id: 'auto', name: 'Auto', multiplier: 1} as ModelOption, ...models.filter(m => m.id !== 'auto')],
    [models],
  )

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <Button
          size="small"
          variant="invisible"
          className={styles.pickerButton}
          leadingVisual={CopilotIcon}
          trailingVisual={TriangleDownIcon}
        >
          <span className="sr-only">Model: </span>
          <span data-testid="model-picker-selected">{displayName}</span>
        </Button>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium">
        {isLoading ? (
          <div className={styles.spinnerContainer}>
            <Spinner size="small" />
          </div>
        ) : (
          <ActionList selectionVariant="single">
            <ActionList.Group>
              <ActionList.GroupHeading>Models</ActionList.GroupHeading>
              {allModels.map(model => (
                <ActionList.Item
                  key={model.id}
                  selected={model.id === selectedModelId}
                  onSelect={() => handleSelect(model.id)}
                >
                  {model.id !== 'auto' && model.icon ? (
                    <ActionList.LeadingVisual>
                      <img src={model.icon} alt="" width={16} height={16} className={styles.modelIcon} />
                    </ActionList.LeadingVisual>
                  ) : (
                    <ActionList.LeadingVisual>
                      <CopilotIcon size={16} />
                    </ActionList.LeadingVisual>
                  )}
                  {model.name}
                  {model.multiplier !== undefined && (
                    <ActionList.TrailingVisual>
                      <span className={styles.multiplierBadge}>{Number(model.multiplier.toFixed(2))}x</span>
                    </ActionList.TrailingVisual>
                  )}
                </ActionList.Item>
              ))}
            </ActionList.Group>
          </ActionList>
        )}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
